import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const SSO_ERRORS = {
  sso_negado: 'Acesso negado pela Microsoft. Verifique se sua conta tem permissão no QDoc.',
  sso_invalido: 'Requisicao de login inválida. Tente novamente.',
  sso_expirado: 'A tentativa de login expirou. Tente novamente.',
  sso_state: 'Falha na validação de seguranca do login. Tente novamente.',
  sso_tenant: 'Esta conta não pertence a organizacao autorizada.',
  sso_sem_email: 'Não foi possível obter o e-mail da sua conta Microsoft.',
  sso_indisponivel: 'Login com Microsoft não está configurado.',
  sso_falha: 'Falha ao entrar com a Microsoft. Tente novamente.',
  usuario_desativado: 'Seu usuário está desativado. Procure um administrador.',
  // Sessao unica: entrar num aparelho encerra a sessao do anterior
  sessao_substituida: 'Sua conta foi acessada em outro dispositivo. '
    + 'O sistema permite uma sessão por vez — entre novamente para usar aqui.',
};

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const code = searchParams.get('error');
    if (code) setError(SSO_ERRORS[code] || 'Falha ao entrar.');
  }, [searchParams]);

  useEffect(() => {
    api.get('/auth/sso-config')
      .then(({ data }) => setSsoEnabled(data.sso_enabled))
      .catch(() => setSsoEnabled(false));
  }, []);

  function handleMicrosoftLogin() {
    // Navegacao de verdade, nao XHR: o fluxo OIDC precisa que o browser
    // saia para o login.microsoftonline.com e volte no callback.
    window.location.href = '/api/auth/azure';
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      navigate(data.must_change_password ? '/change-password' : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-base">
      <div className="w-[380px]">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-brand tracking-tight">QDoc</h1>
          <p className="text-sm text-ink-muted mt-1">Gestao de Documentos da Qualidade</p>
        </div>

        <div className="bg-surface-100 border rounded-md p-6">
          {error && (
            <div className="p-3 rounded-md text-sm mb-4 bg-status-rejected/10 text-status-rejected border border-status-rejected/20">
              {error}
            </div>
          )}

          {ssoEnabled && (
            <button
              type="button"
              onClick={handleMicrosoftLogin}
              className="btn w-full flex items-center justify-center gap-2 border bg-surface-100 hover:bg-surface-base"
            >
              <MicrosoftLogo />
              Entrar com Microsoft
            </button>
          )}

          {ssoEnabled && (
            <div className="flex items-center gap-3 my-5">
              <div className="h-px flex-1 bg-current opacity-10" />
              <span className="text-xs text-ink-muted">ou</span>
              <div className="h-px flex-1 bg-current opacity-10" />
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-secondary mb-1">Email</label>
              <input type="email" className="form-input" value={email}
                onChange={(e) => setEmail(e.target.value)} required autoFocus />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-secondary mb-1">Senha</label>
              <input type="password" className="form-input" value={password}
                onChange={(e) => setPassword(e.target.value)} required />
            </div>

            <button type="submit" className="btn btn-primary w-full mt-2" disabled={loading}>
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          {ssoEnabled && (
            // Com SSO ativo a senha local vale so para administradores; sem este
            // aviso o usuario comum tenta, leva 403 e nao entende por que.
            <p className="text-xs text-ink-muted text-center mt-4">
              Senha local reservada a administradores.
            </p>
          )}
        </div>

        {/* Quem recebeu um PDF nosso e quer conferir a origem nao tem conta
            aqui. Sem este link, so chegaria a verificacao pelo QR. */}
        <p className="text-xs text-ink-muted text-center mt-6">
          Recebeu um documento impresso?{' '}
          <a href="/verificar" className="text-brand hover:underline">Verifique a autenticidade</a>
        </p>
      </div>
    </div>
  );
}
